import { ChangeDetectionStrategy, Component, computed, inject, input, output, Signal } from '@angular/core';

import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { BookingAndAppState } from '@fcom/common/interfaces/booking';
import { isFinnairNorraOrWetLease } from '@fcom/core/utils';
import { cartDataSelector } from '@fcom/booking/store/selectors';
import { FinnairBoundItem, FinnairItineraryItemFlight, FinnairPassengerCode } from '@fcom/dapi/api/models';
import { Amount } from '@fcom/dapi/interfaces';

import { filterServices } from '../../../modules/ancillaries/utils';
import { isFlight } from '../../../utils/flight.utils';
import { getServicesForBoundWithUpsell } from '../../../utils/common-booking.utils';
import { getSummaryForCategory, ServiceType } from '../../../utils/services-summary.utils';
import { categoryStatusMapSelector, cityNameByLocationCodeSelector } from '../../../store';
import { BENEFITS } from '../../../utils/benefit.utils';
import {
  filterServiceItemByFlight,
  filterServiceItemByPassenger,
  multiplyLabelAmount,
  routeLabel,
} from '../../../utils/shopping-cart.utils';

interface BenefitRow {
  title?: string;
  titleKey?: string;
  count?: number;
}

interface BenefitGroup {
  key: string;
  routeLabel?: string;
  benefits: BenefitRow[];
}

const isOperatedByFinnairGroup = (flight: FinnairItineraryItemFlight): boolean =>
  isFinnairNorraOrWetLease(flight.operatingAirline?.name, flight.operatingAirline?.code);

const benefitRowKey = (row: BenefitRow): string => `${row.titleKey ?? ''}|${row.title ?? ''}|${row.count ?? ''}`;

const dedupeBenefitRows = (rows: BenefitRow[]): BenefitRow[] => {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = benefitRowKey(row);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

@Component({
  selector: 'fin-shopping-cart-bound-fare',
  standalone: false,
  templateUrl: './shopping-cart-bound-fare.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShoppingCartBoundFareComponent {
  protected readonly SvgLibraryIcon = SvgLibraryIcon;

  private store$ = inject<Store<BookingAndAppState>>(Store);

  bound = input.required<FinnairBoundItem>();
  selectedPassengerId = input<string | null>(null);
  price = input<Amount>();
  passengerCount = input<number>();

  openFareFamilyDetails = output<void>();

  private ignoredBenefits = ['rebook', 'cancel', 'plusPoints'];

  private cart = this.store$.selectSignal(cartDataSelector);
  private categoryStatus = this.store$.selectSignal(categoryStatusMapSelector);
  private cityNameByLocationCode = this.store$.selectSignal(cityNameByLocationCodeSelector);

  private includedServices = computed(() =>
    filterServices(this.cart()?.services?.unpaid, (service) => service.includedInTicketType)
  );

  private flights = computed<FinnairItineraryItemFlight[]>(() => this.bound().itinerary.filter(isFlight));

  private refundableBenefits: Signal<BenefitRow[]> = computed(() => {
    const refundable = this.cart()?.benefits[this.bound().fareFamily?.code]?.translatedBenefitRows?.find(
      (row) => row.key === BENEFITS.cancel
    );
    return refundable ? [{ title: refundable.label }] : [];
  });

  private changeableBenefits: Signal<BenefitRow[]> = computed(() => {
    const changeable = this.cart()?.benefits[this.bound().fareFamily?.code]?.translatedBenefitRows?.find(
      (row) => row.key === BENEFITS.rebook
    );
    return changeable ? [{ title: changeable.label }] : [];
  });

  private partnerBenefits: Signal<BenefitRow[]> = computed(() => {
    const cart = this.cart();
    if (!cart) {
      return [];
    }
    const bound = this.bound();
    const hasOnlyAdults = cart.passengers.every((pax) => pax.passengerTypeCode === FinnairPassengerCode.ADT);
    if (!hasOnlyAdults) {
      return [{ titleKey: 'shoppingCart.partnerAirlineAllowances' }];
    }
    const passengerCount = cart.passengers.length;
    return (cart.benefits[bound.fareFamily?.code]?.translatedBenefitRows ?? [])
      .filter((benefit) => benefit.isPositive && !this.ignoredBenefits.includes(benefit.key))
      .flatMap((benefit) => (benefit.children ? benefit.children : benefit))
      .map((benefit) => ({ title: multiplyLabelAmount(benefit.label, passengerCount) }));
  });

  private benefitRowsByFlight: Signal<BenefitRow[][]> = computed(() => {
    const cart = this.cart();
    if (!cart) {
      return [];
    }
    const bound = this.bound();
    const flights = this.flights();
    const serviceItems = flights.some(isOperatedByFinnairGroup)
      ? getServicesForBoundWithUpsell(
          cart,
          false,
          this.includedServices(),
          cart.services.servicesOrder,
          bound,
          cart.serviceCatalog,
          this.categoryStatus()
        )
      : [];
    const passengerId = this.selectedPassengerId();
    return flights.map((flight) => {
      if (!isOperatedByFinnairGroup(flight)) {
        return this.partnerBenefits();
      }
      const flightBound = { ...bound, itinerary: [flight] };
      return serviceItems.reduce((rows, item) => {
        const filteredItem = filterServiceItemByPassenger(
          filterServiceItemByFlight(item, flight.id),
          passengerId,
          cart.passengers
        );
        return getSummaryForCategory(item.category, flightBound, filteredItem, flight.isShortHaul)
          .filter((summaryItem) => summaryItem.type !== ServiceType.NON_INCLUDED)
          .reduce((acc, { quantity, text, description }) => {
            acc.push({ title: text, titleKey: description, count: quantity });
            return acc;
          }, rows);
      }, [] as BenefitRow[]);
    });
  });

  protected benefitGroups: Signal<BenefitGroup[]> = computed(() => {
    const fareFamilyRows = [...this.refundableBenefits(), ...this.changeableBenefits()];
    const rowsByFlight = this.benefitRowsByFlight();
    const isOnEveryFlight = (row: BenefitRow): boolean =>
      rowsByFlight.every((rows) => rows.some((candidate) => benefitRowKey(candidate) === benefitRowKey(row)));

    const sharedRows = dedupeBenefitRows(rowsByFlight[0] ?? []).filter(isOnEveryFlight);
    const cityNames = this.cityNameByLocationCode();

    return [
      { key: this.bound().id, benefits: [...fareFamilyRows, ...sharedRows] },
      ...this.flights().map((flight, index) => ({
        key: flight.id,
        routeLabel: routeLabel(flight, cityNames),
        benefits: dedupeBenefitRows(rowsByFlight[index] ?? []).filter((row) => !isOnEveryFlight(row)),
      })),
    ].filter((group) => group.benefits.length > 0);
  });
}
